import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import { apiClient } from '../apiClient';
import type { Problem } from '../api/problems';

interface ProblemsPage {
  problems: Problem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Paginated problem fetch for infinite scrolling. Passes `q` (and optional
 * `difficulty`) server-side so search spans all problems, not just already-loaded
 * pages. Advances page-by-page until the accumulated count reaches `total`.
 */
export function useInfiniteProblems(
  q: string,
  opts: { pageSize?: number; difficulty?: string } = {},
) {
  const { pageSize = 50, difficulty } = opts;
  return useInfiniteQuery({
    queryKey: [...queryKeys.problems.all, 'infinite', { q, pageSize, difficulty }],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ProblemsPage> => {
      const { data } = await apiClient.get('/api/v1/problems', {
        params: { q: q || undefined, difficulty: difficulty || undefined, page: pageParam, limit: pageSize },
      });
      if (data && typeof data === 'object' && 'problems' in data) {
        return data as ProblemsPage;
      }
      const arr: Problem[] = Array.isArray(data) ? data : [];
      return { problems: arr, total: arr.length, page: pageParam, limit: pageSize };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.problems.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60_000,
  });
}

export interface ProblemsFilter {
  difficulty?: string;
  q?: string;
  page?: number;
  tab?: 'all' | 'dsa' | 'sql' | 'genai' | 'assigned';
  tag?: string;
  company?: string;
  status?: string;
  limit?: number;
}

/**
 * `enabled` is a second argument rather than a field on the filter so it never
 * reaches the query key or the request params — a caller that only needs the
 * catalogue as a fallback (see useProblemSequence) can skip the fetch entirely
 * without fragmenting the cache for callers that do want it.
 */
export function useProblems(filter: ProblemsFilter = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.problems.list(filter as Record<string, unknown>),
    queryFn: async () => {
      const { data } = await apiClient.get('/api/v1/problems', { params: filter });
      // API now returns { problems, total, page, limit }
      if (data && typeof data === 'object' && 'problems' in data) return data as { problems: any[]; total: number; page: number; limit: number };
      return { problems: Array.isArray(data) ? data : [], total: 0, page: 1, limit: 50 };
    },
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

export interface ProblemStats {
  problems_solved: number;
  current_streak: number;
  accuracy_this_week: number;
  solved_problem_ids: string[];
}

export function useProblemStats() {
  return useQuery({
    queryKey: queryKeys.problems.stats(),
    queryFn: async () => {
      const { data } = await apiClient.get<ProblemStats>('/api/v1/problems/stats');
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export interface ProblemCompany {
  company: string;
  problem_count: number;
}

export function useProblemCompanies() {
  return useQuery({
    queryKey: [...queryKeys.problems.all, 'companies'],
    queryFn: async () => {
      const { data } = await apiClient.get<ProblemCompany[]>('/api/v1/problems/companies');
      return data ?? [];
    },
    staleTime: 30 * 60_000,
  });
}

export function useProblem(slug: string) {
  return useQuery({
    queryKey: queryKeys.problems.detail(slug),
    queryFn: async () => {
      const { data } = await apiClient.get<Problem>(`/api/v1/problems/${slug}`);
      return data;
    },
    staleTime: 10 * 60_000,
    enabled: !!slug,
  });
}
